import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

// ────────────────────────────────────────────────────────────────
// 1. GLOBAL ROLES
// ────────────────────────────────────────────────────────────────
export type AppRole = 'registered_user' | 'spot_admin' | 'verifier' | 'org_admin' | 'admin' | 'tester' | 'local_guide' | 'partner' | 'moderator' | 'super_admin';
export const ALL_GLOBAL_ROLES: AppRole[] = ['registered_user', 'spot_admin', 'verifier', 'org_admin', 'admin', 'tester', 'local_guide', 'partner', 'moderator', 'super_admin'];

export const ROLE_LABELS: Record<AppRole, string> = {
  registered_user: 'Benutzer',
  spot_admin: 'Spot Admin',
  verifier: 'Verifizierer',
  org_admin: 'Org Admin',
  admin: 'Administrator',
  tester: 'Tester',
  local_guide: 'Local Guide',
  partner: 'Partner',
  moderator: 'Moderator',
  super_admin: 'Super Admin',
};

export const ROLE_COLORS: Record<AppRole, string> = {
  admin: 'bg-destructive/15 text-destructive border-destructive/30',
  super_admin: 'bg-destructive/20 text-destructive border-destructive/40',
  spot_admin: 'bg-primary/15 text-primary border-primary/30',
  verifier: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
  org_admin: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30',
  moderator: 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30',
  tester: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  local_guide: 'bg-teal-500/15 text-teal-700 dark:text-teal-400 border-teal-500/30',
  partner: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 border-indigo-500/30',
  registered_user: 'bg-secondary text-secondary-foreground border-border',
};

// ────────────────────────────────────────────────────────────────
// 2. ORGANISATION ROLES
// ────────────────────────────────────────────────────────────────
export const ORG_ROLES = ['owner', 'admin', 'editor', 'member'] as const;
export type OrgRole = typeof ORG_ROLES[number];

export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  owner: 'Inhaber',
  admin: 'Admin',
  editor: 'Redakteur',
  member: 'Mitglied',
};

export const ORG_ROLE_COLORS: Record<OrgRole, string> = {
  owner: 'bg-destructive/10 text-destructive border-destructive/20',
  admin: 'bg-primary/10 text-primary border-primary/20',
  editor: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
  member: 'bg-secondary text-secondary-foreground border-border',
};

// ────────────────────────────────────────────────────────────────
// 3. STATUS TYPES
// ────────────────────────────────────────────────────────────────
export const MEMBERSHIP_STATUSES = ['invited', 'active', 'pending', 'removed'] as const;
export type MembershipStatus = typeof MEMBERSHIP_STATUSES[number];

export const USER_STATUSES = ['invited', 'active', 'suspended', 'deleted'] as const;
export type UserStatus = typeof USER_STATUSES[number];

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  invited: 'Eingeladen',
  active: 'Aktiv',
  suspended: 'Gesperrt',
  deleted: 'Gelöscht',
};

export const USER_STATUS_COLORS: Record<UserStatus, string> = {
  active: 'bg-primary/15 text-primary',
  invited: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  suspended: 'bg-destructive/15 text-destructive',
  deleted: 'bg-muted text-muted-foreground',
};

// ────────────────────────────────────────────────────────────────
// 4. HOOK — useUserRoles
//
// Thin role-only shim over `get_effective_access` (RBAC SoT).
// Provides roles + admin flags for UI consumers. Feature/permission
// gating is handled by `useAccessControl` / `useEffectiveAccess`.
// ────────────────────────────────────────────────────────────────
export const useUserRoles = () => {
  const { user } = useAuth();

  // Read roles from the central effective-access RPC (RBAC SoT) instead
  // of the deprecated `user_roles` table.
  const query = useQuery({
    queryKey: ['effective-access', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase.rpc('get_effective_access', {
        _user_id: user.id,
      });
      if (error) throw error;
      return data as { roles?: Array<{ role_code: string }> } | null;
    },
    enabled: !!user,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const roles = ((query.data?.roles ?? []) as Array<{ role_code: string }>)
    .map((r) => r.role_code as AppRole);

  /** Check if user has a global role (admin/super_admin implies all) */
  const hasRole = (role: AppRole): boolean => {
    if (roles.includes('admin') || roles.includes('super_admin')) return true;
    return roles.includes(role);
  };

  // Convenience booleans
  const isSuperAdmin = hasRole('admin') || roles.includes('super_admin');
  const isAdmin = isSuperAdmin || hasRole('spot_admin');

  return {
    roles,
    hasRole,
    isAdmin,
    isSuperAdmin,
    loading: query.isLoading,
    isAuthenticated: !!user,
  };
};
